import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ShowSummary, ShowTipTotals } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { formatCoins, formatUsdCents } from '@fetmode/utils';

import { accrueShowEarnings } from './show-live-earnings.js';
import './ShowLiveStatsBar.css';

export interface ShowLiveStatsBarProps {
  liveShow: ShowSummary;
  totals: ShowTipTotals | null;
}

function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${String(hours)}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

export function ShowLiveStatsBar({ liveShow, totals }: ShowLiveStatsBarProps): ReactElement {
  const [now, setNow] = useState(() => Date.now());
  const totalsReceivedAtRef = useRef(Date.now());
  const totalsKey = `${String(totals?.totalCoins ?? 0)}:${String(totals?.modelEarningsCents ?? 0)}`;

  useEffect(() => {
    totalsReceivedAtRef.current = Date.now();
  }, [totalsKey, liveShow.id, liveShow.startedAt]);

  useEffect(() => {
    const started = Date.parse(liveShow.startedAt);
    if (!Number.isFinite(started)) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [liveShow.startedAt, liveShow.id]);

  const started = Date.parse(liveShow.startedAt);
  const elapsedSeconds = Number.isFinite(started) ? Math.max(0, Math.floor((now - started) / 1000)) : 0;
  const extraSeconds = Math.max(0, (now - totalsReceivedAtRef.current) / 1000);
  const coinsPerMinute = liveShow.access.coinsPerMinute;
  const showRate = coinsPerMinute !== null && coinsPerMinute > 0;
  const accrued = accrueShowEarnings({
    settledCoins: totals?.totalCoins ?? 0,
    settledEarningsCents: totals?.modelEarningsCents ?? 0,
    coinsPerMinute,
    billableViewerCount: liveShow.billableViewerCount ?? 0,
    extraSeconds,
    payoutRate: liveShow.access.payoutRate ?? 0.7,
  });
  const earningsCents = accrued.modelEarningsCents;
  const totalCoins = Math.floor(accrued.totalCoins);
  const hasMoney = totalCoins > 0 || earningsCents > 0;
  const spyCount = liveShow.spyCount ?? 0;
  const floorViewers = Math.max(0, liveShow.viewerCount - spyCount);

  return (
    <div className="show-live-stats" role="status" aria-live="polite">
      <div className="show-live-stats__item">
        <span className="show-live-stats__label">{t('common.studio.live.statsShowTime')}</span>
        <span className="show-live-stats__value show-live-stats__value--mono">
          {formatElapsed(elapsedSeconds)}
        </span>
      </div>

      <div className="show-live-stats__item">
        <span className="show-live-stats__label">{t('common.studio.live.statsEarned')}</span>
        <span className="show-live-stats__value">
          {hasMoney
            ? t('common.studio.live.statsEarnedValue')
                .replace('{{usd}}', formatUsdCents(earningsCents))
                .replace('{{coins}}', formatCoins(totalCoins))
            : t('common.studio.live.statsEarnedEmpty')}
        </span>
      </div>

      <div className="show-live-stats__item">
        <span className="show-live-stats__label">{t('common.studio.live.statsViewers')}</span>
        <span className="show-live-stats__value">{String(floorViewers)}</span>
      </div>

      {spyCount > 0 ? (
        <div className="show-live-stats__item">
          <span className="show-live-stats__label">{t('common.studio.live.statsSpies')}</span>
          <span className="show-live-stats__value">{String(spyCount)}</span>
        </div>
      ) : null}

      {showRate ? (
        <div className="show-live-stats__item">
          <span className="show-live-stats__label">{t('common.studio.live.statsRate')}</span>
          <span className="show-live-stats__value">
            {t('common.studio.live.statsRateValue').replace(
              '{{amount}}',
              formatCoins(coinsPerMinute),
            )}
          </span>
        </div>
      ) : null}

      <div className="show-live-stats__type" title={liveShow.access.typeDescription}>
        {liveShow.access.typeLabel}
      </div>
    </div>
  );
}
